import React from 'react';
import { MILITARY, MILITARY_SELECTIVE } from 'config/formSections/military';
import { i18n } from 'config/locales';
import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import NotApplicable from 'components/Form/NotApplicable';
import Field from 'components/Form/Field';
import Textarea from 'components/Form/Textarea';
import Text from 'components/Form/Text';
import * as formTypes from 'constants/formTypes';
import { hideSelectiveService } from '../../../../validators/selectiveservice';
import { isFormSF852013 } from 'helpers/form';
import ErrorMessage from 'components/ErrorMessage';
import './Selective.scss';
import Comments from 'components/Form/Comments';

const sectionConfig = {
  key: MILITARY_SELECTIVE.key,
  section: MILITARY.name,
  store: MILITARY.store,
  subsection: MILITARY_SELECTIVE.name,
  storeKey: MILITARY_SELECTIVE.storeKey,
};

export class Selective extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;

    this.update = this.update.bind(this);
    this.updateBornAfter = this.updateBornAfter.bind(this);
    this.updateRegistered = this.updateRegistered.bind(this);
    this.updateRegisteredNotApplicable = this.updateRegisteredNotApplicable.bind(this);
    this.updateRegistrationNumber = this.updateRegistrationNumber.bind(this);
    this.updateExplanation = this.updateExplanation.bind(this);
    this.updateComments = this.updateComments.bind(this);
    this.updateAnswerComments = this.updateAnswerComments.bind(this);
    this.updateRegistrationNumberComments = this.updateRegistrationNumberComments.bind(this);
    this.updateDNKComments = this.updateDNKComments.bind(this);
    this.updateLegalExemptionComments = this.updateLegalExemptionComments.bind(this);
  }

  update(queue) {
    this.props.onUpdate(this.storeKey, {
      WasBornAfter: this.props.WasBornAfter,
      HasRegistered: this.props.HasRegistered,
      HasRegisteredNotApplicable: this.props.HasRegisteredNotApplicable || { applicable: true },
      RegistrationNumber: this.props.RegistrationNumber,
      Explanation: this.props.Explanation,
      Comments: this.props.Comments,
      AnswerComments: this.props.AnswerComments,
      RegistrationNumberComments: this.props.RegistrationNumberComments,
      DNKComments: this.props.DNKComments,
      LegalExemptionComments: this.props.LegalExemptionComments,
      ...queue,
    });
  }

  updateBornAfter(values) {
    const {
      HasRegistered,
      HasRegisteredNotApplicable,
      RegistrationNumber,
      Explanation,
    } = this.props;
    const emptyValue = { value: '' };
    const wasBornAfterNo = values?.value === 'No';

    // If there is no history clear out any previously entered data
    this.update({
      WasBornAfter: values,
      HasRegistered: wasBornAfterNo ? emptyValue : HasRegistered,
      HasRegisteredNotApplicable: wasBornAfterNo ? { applicable: true } : HasRegisteredNotApplicable,
      RegistrationNumber: wasBornAfterNo ? emptyValue : RegistrationNumber,
      Explanation: wasBornAfterNo ? emptyValue : Explanation,
    });
  }

  updateRegistered(values) {
    const emptyValue = { value: '' };
    // If there is no history clear out any previously entered data
    this.update({
      HasRegistered: values,
      RegistrationNumber: values?.value === 'Yes' ? this.props.RegistrationNumber : emptyValue,
      RegistrationNumberComments: values?.value === 'Yes' ? this.props.RegistrationNumberComments : emptyValue,
      Explanation: values?.value === 'Yes' ? emptyValue : this.props.Explanation,
      LegalExemptionComments: values?.value === 'Yes' ? emptyValue : this.props.LegalExemptionComments,
      DNKComments: emptyValue,
    });
  }

  updateRegisteredNotApplicable(values) {
    const emptyValue = { value: '' };
    const notApplicable = values?.applicable === false;

    // If there is no history clear out any previously entered data
    this.update({
      HasRegisteredNotApplicable: values,
      HasRegistered: notApplicable ? emptyValue : this.props.HasRegistered,
      RegistrationNumber: notApplicable ? emptyValue : this.props.RegistrationNumber,
      RegistrationNumberComments: notApplicable ? emptyValue : this.props.RegistrationNumberComments,
      LegalExemptionComments: notApplicable ? emptyValue : this.props.LegalExemptionComments,
      DNKComments: notApplicable ? this.props.DNKComments : emptyValue,
    });
  }

  updateRegistrationNumber(value) {
    this.update({
      RegistrationNumber: value,
    });
  }

  updateExplanation(value) {
    this.update({
      Explanation: value,
    });
  }

  updateComments(value) {
    this.update({
      Comments: value,
    });
  }

  updateAnswerComments(value) {
    this.update({
      AnswerComments: value,
    });
  }

  updateRegistrationNumberComments(value) {
    this.update({
      RegistrationNumberComments: value,
    });
  }

  updateDNKComments(value) {
    this.update({
      DNKComments: value,
    });
  }

  updateLegalExemptionComments(value) {
    this.update({
      LegalExemptionComments: value,
    });
  }

  renderSupportLink = () => (
    <div className="field">
      <div className="table">
        <div className="messages">
          <div className="message help">
            <strong>{i18n.t('military.selective.help.remember.title')}</strong>
            {i18n.m('military.selective.help.remember.message')}
          </div>
        </div>
      </div>
    </div>
  );

  render() {
    const { formType, formVersion } = this.props;
    const wasBornAfterMale = !hideSelectiveService({
      Identification: {
        ApplicantBirthDate: {
          Date: this.props.applicantBirthdate,
        },
        Physical: { Sex: this.props.applicantSex },
      },
    });

    return (
      <div
        className="section-content selective"
        data-section={MILITARY.key}
        data-subsection={MILITARY_SELECTIVE.key}
      >
        <h1 className="section-header">{i18n.t('military.destination.selective')}</h1>

        {/*
          This is a bit of a departure from typical validation, as this is a warning message
        */}

        {this.props.WasBornAfter?.value === 'No' && wasBornAfterMale && (
          <ErrorMessage
            isWarning
            title="Oops, there may be a problem."
            message={[
              'Your birthdate and sex indicates you are a male born after this date.',
              'Please check your answer.'
            ]}
          />
        )}

        {this.props.WasBornAfter.value === 'Yes' && this.props.applicantSex?.value === 'Female' && (
          <ErrorMessage
            isWarning
            title="Oops, there may be a problem."
            message={[
              'Your sex indicates you are not a male.',
              'Please check your answer.'
            ]}
          />
        )}

        <Branch
          name="was_bornafter"
          label={i18n.t('military.selective.heading.born')}
          labelSize="h4"
          className="born"
          {...this.props.WasBornAfter}
          help="military.selective.help.born"
          warning
          onUpdate={this.updateBornAfter} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          scrollIntoView={this.props.scrollIntoView}
          attribute="WasBornAfter"
        />
        {!isFormSF852013(formType, formVersion) && (
          <Comments
            title={i18n.t('military.selective.comments')}
            name="RegistrationRequiredComment"
            attribute="RegistrationRequiredComment"
            className="registration-required-comment"
            {...this.props.Comments}
            onUpdate={this.updateComments}
            onError={this.props.onError}
            required={false}
          />
        )}

        <Show when={this.props.WasBornAfter.value === 'Yes'}>
          <div>
            <Show when={this.props.formType !== formTypes.SF852013}>
              <NotApplicable
                {...this.props.HasRegisteredNotApplicable}
                name="HasRegisteredNotApplicable"
                label={i18n.t('military.selective.label.idk')}
                ariaLabel={i18n.t('military.selective.aria.idk')}
                or={i18n.m('military.selective.para.or')}
                onError={this.props.onError}
                onUpdate={this.updateRegisteredNotApplicable} // eslint-disable-line @typescript-eslint/unbound-method
                attribute="HasRegisteredNotApplicable"
                includeNAConfirmation={
                  Boolean(this.props.RegistrationNumber?.value
                  || this.props.RegistrationNumberComments?.value
                  || this.props.LegalExemptionComments?.value)
                }
              >
                <Branch
                  name="has_registered"
                  className="registered"
                  label={i18n.t('military.selective.heading.registered')}
                  labelSize="h4"
                  {...this.props.HasRegistered}
                  warning
                  onUpdate={this.updateRegistered} // eslint-disable-line @typescript-eslint/unbound-method
                  required={this.props.required}
                  onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
                  scrollIntoView={this.props.scrollIntoView}
                  attribute="HasRegistered"
                />
              </NotApplicable>
              {!isFormSF852013(formType, formVersion) && (
                <Comments
                  title={i18n.t('military.selective.answercomments')}
                  name="AnswerComment"
                  attribute="AnswerComment"
                  className="answer-comment"
                  {...this.props.AnswerComments}
                  onUpdate={this.updateAnswerComments}
                  onError={this.props.onError}
                  required={false}
                  negativeMarginTop={false}
                />
              )}
            </Show>
            <Show when={this.props.formType === formTypes.SF852013}>
              <Branch
                name="has_registered"
                className="registered"
                label={i18n.t('military.selective.heading.registered')}
                labelSize="h4"
                {...this.props.HasRegistered}
                warning
                onUpdate={this.updateRegistered} // eslint-disable-line @typescript-eslint/unbound-method
                required={this.props.required}
                onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
                scrollIntoView={this.props.scrollIntoView}
                attribute="HasRegistered"
              />
            </Show>
            <Show when={this.props.HasRegistered.value === 'Yes'}>
              <div>
                <Field
                  title={i18n.t('military.selective.heading.number')}
                  className="no-margin-bottom"
                  adjustFor="labels"
                  scrollIntoView={this.props.scrollIntoView}
                  attribute="RegistrationNumber"
                >
                  <Text
                    name="RegistrationNumber"
                    className="registration-number"
                    label={i18n.t('military.selective.label.number')}
                    {...this.props.RegistrationNumber}
                    pattern="^[\d]*$"
                    prefix="selective"
                    onUpdate={this.updateRegistrationNumber} // eslint-disable-line @typescript-eslint/unbound-method
                    onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
                    required={this.props.required}
                    attribute="RegistrationNumber"
                  />
                </Field>
                {this.renderSupportLink()}
              </div>
              {!isFormSF852013(formType, formVersion) && (
                <Comments
                  title={i18n.t('military.selective.numbercomments')}
                  name="RegistrationNumberComments"
                  attribute="RegistrationNumberComments"
                  className="registration-number-comment"
                  {...this.props.RegistrationNumberComments}
                  onUpdate={this.updateRegistrationNumberComments}
                  onError={this.props.onError}
                  required={false}
                />
              )}
            </Show>
            <Show
              when={
                this.props.HasRegistered.value === 'No'
                || (this.props.HasRegisteredNotApplicable?.applicable === false && this.props.formType !== formTypes.SF852013)
              }
            >
              <Field
                title={i18n.t('military.selective.label.explanation')}
                titleSize="h4"
                className={this.props.HasRegistered.value === 'No' ? 'explanation-field-no' : 'explanation-field-idk'}
                help="military.selective.help.explanation"
                adjustFor="textarea"
                scrollIntoView={this.props.scrollIntoView}
                attribute="Explanation"
              >
                <Textarea
                  name="Explanation"
                  className="explanation"
                  {...this.props.Explanation}
                  onUpdate={this.updateExplanation} // eslint-disable-line @typescript-eslint/unbound-method
                  onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
                  required={this.props.required}
                  attribute="Explanation"
                />
              </Field>
              {this.props.HasRegisteredNotApplicable?.applicable === false && (
                <>
                  {this.renderSupportLink()}
                  <Comments
                    title={i18n.t('military.selective.dnkcomments')}
                    name="DNKComments"
                    attribute="DNKComments"
                    className="dnk-comments"
                    {...this.props.DNKComments}
                    onUpdate={this.updateDNKComments}
                    onError={this.props.onError}
                    required={false}
                  />
                </>
              )}
              {!isFormSF852013(formType, formVersion) && this.props.HasRegistered.value === 'No' && (
                <Comments
                  title={i18n.t('military.selective.exemptioncomments')}
                  name="LegalExemptionComments"
                  attribute="LegalExemptionComments"
                  className="exemption-comments"
                  {...this.props.LegalExemptionComments}
                  onUpdate={this.updateLegalExemptionComments}
                  onError={this.props.onError}
                  required={false}
                />
              )}
            </Show>
          </div>
        </Show>
      </div>
    );
  }
}

Selective.defaultProps = {
  WasBornAfter: {},
  HasRegistered: { value: '' },
  HasRegisteredNotApplicable: { applicable: true },
  RegistrationNumber: {},
  Explanation: {},
  Comments: {},
  AnswerComments: {},
  RegistrationNumberComments: {},
  DNKComments: {},
  LegalExemptionComments: {},
  onUpdate: () => { },
  onError: (value, arr) => arr,
  section: 'military',
  subsection: 'selective',
  dispatch: () => { },
  errors: [],
};

export default connectSubsection(Selective, sectionConfig);
